import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {
  StyledTableCell,
  StyledCheckbox,
  StyledTableContainer,
  StyledKeyboardArrowDownIcon,
  StyledKeyboardArrowUpIcon,
  StyledSortingDiv,
  StyledLoaderBox
} from './Styles'
import {CircularProgress} from '@mui/material'
import {useEffect, useState} from 'react'
import CustomPagination from '../CustomPagination'
import {getColumnLabel} from '../../../helper/getColumnLabelsHelper'
import LoadingError from '../../Common/Error/DataLoadingError/LoadingError'
import NoData from '../../Common/Error/NoDataFound/NoData'
import TableDataComponent from './TableDataComponent'
import {count} from '../../../store/prospectSegment/allProspectsCount/allProspectsCountSlice'
import {useDispatch, useSelector} from 'react-redux'
import PropTypes from 'prop-types'
import {
  setSortColumn,
  setSortDirection,
  setSortConfig as setSortConfigAction
} from '../../../store/prospectSegment/allProspectsData/allProspectsDataSlice'
import {getCamelCaseToSnakeCase} from '../../../helper/getCamelCaseToTitleCase'
import theme from '../../../themes/index'

function isJsonString(str) {
  try {
    return typeof JSON.parse(str) === 'object'
  } catch {
    return false
  }
}

const TableComponent = ({
  loading,
  error,
  headers,
  explicitKeyLabelMapping,
  refetch,
  height,
  handleChangePage,
  handleSelectAll,
  handleRowCheckboxChange,
  selectAll,
  rowData,
  setRowData,
  rows,
  actionMenuList,
  handleGenerateEmailClick,
  handleTableRowClick,
  handleNameClick,
  totalCount = 0,
  isActionRequired = false,
  hasAccordian = false,
  showAddColumnHeader = false,
  addColumnComponent = null,
  showCheckboxes = false,
  selectedItemCurrentPageNumber = 1,
  generateEmail = false,
  contactReport = '',
  currentTab = '',
  disabledEmail = false,
  resetFlag = false,
  setResetFlag,
  prospectList,
  statesValue = [],
  excludedSorting = [],
  handleDataSort
}) => {
  const sortConfigState = useSelector((state) => state.getAllProspectsData.sortConfig)
  const [sortConfig, setSortConfig] = useState(sortConfigState)
  const dispatch = useDispatch()
  const [updatedData, setUpdatedData] = useState(rowData)
  const [resetInterestToggle, setResetInterestToggle] = useState(false)

  const handleDirectionChange = () => {
    return sortConfig.direction === 'desc' ? 'desc' : 'asc'
  }

  useEffect(() => {
    setSortConfig(sortConfigState)
  }, [sortConfigState])

  const handleSort = (key, direction) => {
    if (sortConfig.key === key) {
      // If the same key is clicked again, reverse the sorting direction
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc'
    } else {
      // If a different key is clicked, reset the sorting direction to 'desc'
      // if sorting order is already 'desc' else reverse the order
      direction = sortConfig.direction === 'asc' ? 'desc' : handleDirectionChange()
    }
    setSortConfig({key, direction})
    dispatch(setSortColumn(getCamelCaseToSnakeCase(key === 'fullName' ? 'name' : key)))
    dispatch(setSortDirection(direction))
    dispatch(setSortConfigAction({direction, key}))
    handleDataSort()
    setResetInterestToggle(!resetInterestToggle)
  }

  useEffect(() => {
    if (updatedData?.length > 0) {
      setRowData(updatedData)
      setUpdatedData([])
    } else {
      setRowData(prospectList)
    }
  }, [updatedData, prospectList, setRowData])

  useEffect(() => {
    if (resetFlag) {
      setUpdatedData([])
      setResetFlag(false)
    }
  }, [resetFlag, setResetFlag])

  if (error && !loading) {
    const {message} = error
    const formatMessage = message.replaceAll("'", '"')
    currentTab === 'allProspects' && dispatch(count(0))
    return isJsonString(formatMessage) && JSON.parse(formatMessage).status_code === 404 ? (
      <NoData height={height} sx={{minHeight: '700px'}} />
    ) : (
      <LoadingError onTryAgain={refetch} height={height} />
    )
  }

  const getSortIcon = (headerName) => {
    if (sortConfig.key === headerName && sortConfig.direction === 'desc') {
      return <StyledKeyboardArrowDownIcon />
    }
    if (sortConfig.key === headerName) {
      return <StyledKeyboardArrowUpIcon sx={{color: theme.palette.common.blue1}} />
    }
    return <StyledKeyboardArrowUpIcon />
  }

  const disableSorting = ({headerName}) => {
    return excludedSorting.includes(headerName) ? (
      <StyledTableCell key={headerName}>
        <StyledSortingDiv>{getColumnLabel(explicitKeyLabelMapping, headerName)}</StyledSortingDiv>
      </StyledTableCell>
    ) : (
      <StyledTableCell key={headerName} onClick={() => handleSort(headerName, sortConfig.direction)}>
        <StyledSortingDiv>
          {getColumnLabel(explicitKeyLabelMapping, headerName)}
          {getSortIcon(headerName)}
        </StyledSortingDiv>
      </StyledTableCell>
    )
  }

  return (
    <>
      <StyledTableContainer component="paper">
        <Table aria-label="table" size="small" sx={{minHeight: loading ? '700px' : ''}}>
          {loading && (
            <StyledLoaderBox>
              <CircularProgress />
            </StyledLoaderBox>
          )}
          {!loading && (
            <>
              <TableHead size="small" sx={{backgroundColor: (theme) => theme.palette.primary.main, height: '53px'}}>
                <TableRow colSpan="3">
                  {showCheckboxes && (
                    <TableCell>
                      <StyledCheckbox
                        size="small"
                        color="primary"
                        inputProps={{
                          'aria-label': 'bookmark'
                        }}
                        checked={selectAll}
                        onChange={(event) => handleSelectAll(event)}
                      />
                    </TableCell>
                  )}

                  {headers?.map((key) => {
                    return key === 'interestTag' ? (
                      <StyledTableCell key={key}>
                        <StyledSortingDiv>{getColumnLabel(explicitKeyLabelMapping, key)}</StyledSortingDiv>
                      </StyledTableCell>
                    ) : (
                      disableSorting({headerName: key})
                    )
                  })}

                  <StyledTableCell sx={{textAlign: 'right'}}>
                    {showAddColumnHeader && addColumnComponent ? addColumnComponent : null}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowData?.map((obj) => (
                  <TableDataComponent
                    row={obj}
                    key={obj.uniqueConstituentIdentifier}
                    headers={headers}
                    handleRowCheckboxChange={(event) => handleRowCheckboxChange(event, obj)}
                    showAddColumnHeader={showAddColumnHeader}
                    showCheckboxes={showCheckboxes}
                    isActionRequired={isActionRequired}
                    hasAccordian={hasAccordian}
                    actionMenuList={actionMenuList}
                    generateEmail={generateEmail}
                    handleGenerateEmailClick={handleGenerateEmailClick}
                    contactReport={contactReport}
                    handleTableRowClick={handleTableRowClick}
                    disabledEmail={disabledEmail}
                    handleNameClick={handleNameClick}
                    resetInterestToggle={resetInterestToggle}
                    statesValue={statesValue}
                  />
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </StyledTableContainer>
      <CustomPagination
        count={totalCount}
        page={selectedItemCurrentPageNumber}
        rowsPerPage={rows}
        onChangePage={handleChangePage}
      />
    </>
  )
}

TableComponent.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  headers: PropTypes.object,
  explicitKeyLabelMapping: PropTypes.array,
  refetch: PropTypes.object,
  height: PropTypes.number,
  handleChangePage: PropTypes.func,
  handleSelectAll: PropTypes.func,
  handleRowCheckboxChange: PropTypes.func,
  selectAll: PropTypes.bool,
  rowData: PropTypes.array,
  setRowData: PropTypes.func,
  rows: PropTypes.number,
  actionMenuList: PropTypes.array,
  totalCount: PropTypes.number,
  handleGenerateEmailClick: PropTypes.func,
  handleTableRowClick: PropTypes.func,
  isActionRequired: PropTypes.bool,
  hasAccordian: PropTypes.bool,
  showAddColumnHeader: PropTypes.bool,
  addColumnComponent: PropTypes.object,
  showCheckboxes: PropTypes.bool,
  selectedItemCurrentPageNumber: PropTypes.number,
  generateEmail: PropTypes.bool,
  contactReport: PropTypes.string,
  currentTab: PropTypes.string,
  disabledEmail: PropTypes.bool,
  handleNameClick: PropTypes.func,
  resetFlag: PropTypes.bool,
  setResetFlag: PropTypes.func,
  prospectList: PropTypes.array,
  statesValue: PropTypes.array,
  excludedSorting: PropTypes.array,
  handleDataSort: PropTypes.func
}

export default TableComponent
